import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Select,
  InputNumber,
  Button,
  Table,
  Tag,
  Space,
  message,
  Divider,
  Typography,
  Form,
  Popconfirm,
  Modal,
  Descriptions,
} from 'antd';
import {
  DollarOutlined,
  ReloadOutlined,
  CheckOutlined,
  DeleteOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../../config/apiClient';
import MainLayout from '../../layout/layout';
import { usePermissions } from '../../../context/permissionsContext';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const CuentaCorriente = () => {
  const { hasPermission } = usePermissions();
  const [jugadores, setJugadores] = useState([]);
  const [jugadorSeleccionado, setJugadorSeleccionado] = useState(null);
  const [estadoCuenta, setEstadoCuenta] = useState(null);
  const [formasPago, setFormasPago] = useState([]);
  const [jugadoresConSaldo, setJugadoresConSaldo] = useState([]);
  const [loadingJugadoresSaldo, setLoadingJugadoresSaldo] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingPago, setLoadingPago] = useState(false);
  const [montoPago, setMontoPago] = useState(0);
  const [formaPagoSeleccionada, setFormaPagoSeleccionada] = useState(null);
  const [form] = Form.useForm();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [modalDetalleVisible, setModalDetalleVisible] = useState(false);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [detalleMovimiento, setDetalleMovimiento] = useState(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadJugadores = async () => {
    try {
      const response = await apiClient.get('/club/jugadores');
      if (response.ok) {
        const data = await response.json();
        setJugadores(data || []);
      }
    } catch (error) {
      console.error('Error al cargar jugadores:', error);
    }
  };

  const loadFormasPago = async () => {
    try {
      const response = await apiClient.get('/gestion/formas-pago');
      if (response.ok) {
        const data = await response.json();
        // Mostrar todas las formas de pago activas excepto "Cuenta Corriente"
        setFormasPago(data.filter(fp => 
          fp.fop_activo && 
          fp.fop_nombre?.toUpperCase().trim() !== 'CUENTA CORRIENTE'
        ));
      }
    } catch (error) {
      console.error('Error al cargar formas de pago:', error);
    }
  };

  const loadEstadoCuenta = async (jugadorId) => {
    if (!jugadorId) {
      setEstadoCuenta(null);
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.get(`/gestion/cuenta-corriente/estado?jugador_id=${jugadorId}`);
      if (response.ok) {
        const data = await response.json();
        setEstadoCuenta(data);
      } else {
        message.error('Error al cargar el estado de cuenta');
        setEstadoCuenta(null);
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar el estado de cuenta');
      setEstadoCuenta(null);
    } finally {
      setLoading(false);
    }
  };

  const loadJugadoresConSaldo = async () => {
    setLoadingJugadoresSaldo(true);
    try {
      const response = await apiClient.get('/gestion/cuenta-corriente/jugadores-con-saldo');
      if (response.ok) {
        const data = await response.json();
        // Filtrar solo los que tienen saldo positivo (deudores)
        const deudores = (data || []).filter(j => parseFloat(j.saldo_actual || 0) > 0);
        setJugadoresConSaldo(deudores);
      }
    } catch (error) {
      console.error('Error al cargar jugadores con saldo:', error);
    } finally {
      setLoadingJugadoresSaldo(false);
    }
  };

  useEffect(() => {
    loadJugadores();
    loadFormasPago();
    loadJugadoresConSaldo();
  }, []);

  useEffect(() => {
    if (jugadorSeleccionado) {
      loadEstadoCuenta(jugadorSeleccionado);
    } else {
      setEstadoCuenta(null);
    }
  }, [jugadorSeleccionado]);

  const handleRegistrarPago = async () => {
    if (!jugadorSeleccionado) {
      message.warning('Seleccione un jugador');
      return;
    }

    if (!montoPago || montoPago <= 0) {
      message.warning('Ingrese un monto válido');
      return;
    }

    if (!estadoCuenta) {
      message.warning('Cargue primero el estado de cuenta');
      return;
    }

    const saldoActual = parseFloat(estadoCuenta.saldo_actual || 0);
    const montoPagoNum = parseFloat(montoPago || 0);
    
    // Validación estricta
    if (isNaN(montoPagoNum) || montoPagoNum <= 0) {
      message.warning('Ingrese un monto válido mayor a 0');
      return;
    }
    
    if (isNaN(saldoActual)) {
      message.error('Error al obtener el saldo actual. Por favor, recargue la página.');
      return;
    }
    
    if (montoPagoNum > saldoActual) {
      message.error(`El monto del pago ($${montoPagoNum.toFixed(2)}) no puede ser mayor al saldo actual ($${saldoActual.toFixed(2)})`);
      return;
    }

    setLoadingPago(true);
    try {
      const response = await apiClient.post('/gestion/cuenta-corriente/pago', {
        jugador_id: jugadorSeleccionado,
        monto: montoPagoNum, // Enviar el número parseado
        forma_pago_id: formaPagoSeleccionada || null,
      });

      if (response.ok) {
        message.success('Pago registrado correctamente');
        setMontoPago(0);
        setFormaPagoSeleccionada(null);
        form.resetFields();
        // Recargar estado de cuenta y lista de jugadores con saldo
        await loadEstadoCuenta(jugadorSeleccionado);
        await loadJugadoresConSaldo();
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al registrar el pago');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al registrar el pago');
    } finally {
      setLoadingPago(false);
    }
  };

  const handleEliminarMovimiento = async (movimientoId) => {
    try {
      const response = await apiClient.delete(`/gestion/cuenta-corriente/movimiento/${movimientoId}`);
      
      if (response.ok) {
        message.success('Movimiento eliminado correctamente');
        // Recargar estado de cuenta y lista de jugadores con saldo
        await loadEstadoCuenta(jugadorSeleccionado);
        await loadJugadoresConSaldo();
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al eliminar el movimiento');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al eliminar el movimiento');
    }
  };

  const handleVerDetalleMovimiento = async (record) => {
    setModalDetalleVisible(true);
    setDetalleMovimiento(null);
    if (record.ccm_referencia_tipo === 'pago_manual') {
      setDetalleMovimiento({ tipo: 'pago_manual', movimiento: record });
      return;
    }
    if (record.ccm_referencia_tipo === 'venta') {
      const ids = (record._refIds && record._refIds.length > 0) ? record._refIds : [record.ccm_referencia_id];
      setLoadingDetalle(true);
      try {
        const ventas = [];
        for (const id of ids) {
          const response = await apiClient.get(`/gestion/ventas/${id}`);
          if (response.ok) ventas.push(await response.json());
        }
        setDetalleMovimiento({ tipo: 'venta', ventas });
      } catch (error) {
        console.error('Error:', error);
        message.error('Error al cargar el detalle');
        setModalDetalleVisible(false);
      } finally {
        setLoadingDetalle(false);
      }
    } else {
      setDetalleMovimiento({ tipo: 'otro', movimiento: record });
    }
  };

  // Agrupar movimientos del mismo cobro unificado (misma fecha/minuto, tipo y observaciones) en una sola fila
  const movimientosParaTabla = useMemo(() => {
    const list = estadoCuenta?.movimientos || [];
    if (list.length === 0) return [];
    const key = (m) => `${dayjs(m.ccm_fecha).format('YYYY-MM-DD HH:mm')}_${m.ccm_tipo_movimiento}_${(m.ccm_observaciones || '').trim()}`;
    const grupos = {};
    list.forEach((m) => {
      const k = key(m);
      if (!grupos[k]) {
        grupos[k] = {
          ...m,
          ccm_monto: 0,
          _refIds: [],
          _grouped: false,
        };
      }
      const g = grupos[k];
      g.ccm_monto = (parseFloat(g.ccm_monto) || 0) + (parseFloat(m.ccm_monto) || 0);
      if (m.ccm_referencia_tipo === 'venta' && m.ccm_referencia_id != null) {
        if (!g._refIds.includes(m.ccm_referencia_id)) g._refIds.push(m.ccm_referencia_id);
      }
    });
    const filas = Object.values(grupos);
    filas.forEach((f) => {
      if (f._refIds.length > 1) f._grouped = true;
      if (f._refIds.length > 0) f._refIds.sort((a, b) => a - b);
    });
    return filas.sort((a, b) => new Date(b.ccm_fecha) - new Date(a.ccm_fecha));
  }, [estadoCuenta?.movimientos]);

  const columnsMovimientos = [
    {
      title: 'Fecha',
      dataIndex: 'ccm_fecha',
      key: 'ccm_fecha',
      width: 150,
      render: (fecha) => dayjs(fecha).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: 'Tipo',
      dataIndex: 'ccm_tipo_movimiento',
      key: 'ccm_tipo_movimiento',
      width: 100,
      render: (tipo) => (
        <Tag color={tipo === 'DEUDA' ? 'red' : 'green'}>
          {tipo}
        </Tag>
      ),
    },
    {
      title: 'Referencia',
      key: 'referencia',
      width: 150,
      render: (_, record) => {
        if (record._refIds && record._refIds.length > 1) {
          return `Ventas #${record._refIds.join(', #')}`;
        }
        if (record.ccm_referencia_tipo === 'venta') {
          return `Venta #${record.ccm_referencia_id}`;
        }
        if (record.ccm_referencia_tipo === 'pago_manual') {
          return 'Pago manual';
        }
        return '-';
      },
    },
    {
      title: 'Monto',
      dataIndex: 'ccm_monto',
      key: 'ccm_monto',
      width: 120,
      align: 'right',
      render: (monto) => `$${Number(monto).toFixed(2)}`,
    },
    {
      title: 'Observaciones',
      dataIndex: 'ccm_observaciones',
      key: 'ccm_observaciones',
      ellipsis: true,
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 140,
      align: 'center',
      render: (_, record) => (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleVerDetalleMovimiento(record)}
            style={{ padding: 0 }}
          >
            Ver
          </Button>
          {!record._grouped && record.ccm_tipo_movimiento === 'PAGO' && record.ccm_referencia_tipo === 'pago_manual' && (
            <Popconfirm
              title="¿Estás seguro de eliminar este pago?"
              description="Esta acción no se puede deshacer."
              onConfirm={() => handleEliminarMovimiento(record.ccm_id)}
              okText="Sí, eliminar"
              cancelText="Cancelar"
              okButtonProps={{ danger: true }}
            >
              <Button type="link" danger size="small" icon={<DeleteOutlined />} style={{ padding: 0 }}>
                Eliminar
              </Button>
            </Popconfirm>
          )}
        </div>
      ),
    },
  ];

  const jugadorSeleccionadoObj = jugadores.find(j => j.jcl_id === jugadorSeleccionado);

  const columnsJugadoresSaldo = [
    {
      title: 'Jugador',
      key: 'jugador',
      render: (_, record) => (
        <span style={{ color: '#ffffff' }}>
          {record.jcl_nombre} {record.jcl_apellido}
        </span>
      ),
    },
    {
      title: 'Total Deuda',
      dataIndex: 'total_deuda',
      key: 'total_deuda',
      align: 'right',
      render: (monto) => (
        <span style={{ color: '#ff4d4f' }}>
          ${parseFloat(monto || 0).toFixed(2)}
        </span>
      ),
    },
    {
      title: 'Total Pagos',
      dataIndex: 'total_pagos',
      key: 'total_pagos',
      align: 'right',
      render: (monto) => (
        <span style={{ color: '#52c41a' }}>
          ${parseFloat(monto || 0).toFixed(2)}
        </span>
      ),
    },
    {
      title: 'Saldo Actual',
      dataIndex: 'saldo_actual',
      key: 'saldo_actual',
      align: 'right',
      render: (saldo) => {
        const saldoNum = parseFloat(saldo || 0);
        return (
          <span style={{ 
            color: saldoNum > 0 ? '#ff4d4f' : '#52c41a',
            fontWeight: 'bold',
            fontSize: '16px'
          }}>
            ${saldoNum.toFixed(2)}
          </span>
        );
      },
    },
    {
      title: 'Acción',
      key: 'accion',
      align: 'center',
      render: (_, record) => (
        <Button
          type="link"
          onClick={() => setJugadorSeleccionado(record.jcl_id)}
          style={{ color: '#1890ff' }}
        >
          Ver Detalle
        </Button>
      ),
    },
  ];

  return (
    <MainLayout>
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: 16 }}>
          <h1 style={{ margin: 0, marginBottom: 16, color: '#ffffff', fontSize: isMobile ? '20px' : '24px' }}>
            Cuenta Corriente
          </h1>
        </div>

        <Card
          style={{
            backgroundColor: '#2d2d2d',
            border: '1px solid #434343',
            borderRadius: 8,
            marginBottom: 16,
          }}
          headStyle={{
            backgroundColor: '#2d2d2d',
            borderBottom: '1px solid #434343',
            color: '#ffffff',
          }}
          bodyStyle={{
            backgroundColor: '#2d2d2d',
            color: '#e0e0e0',
          }}
        >
          <div style={{ marginBottom: 16 }}>
            <Text strong style={{ color: '#ffffff', display: 'block', marginBottom: 8 }}>
              Seleccionar cuenta
            </Text>
            <Select
              placeholder="Seleccione un jugador"
              value={jugadorSeleccionado}
              onChange={(value) => setJugadorSeleccionado(value)}
              showSearch
              style={{ width: '100%' }}
              filterOption={(input, option) => {
                const text = String(option?.children || option?.label || '').toLowerCase();
                return text.includes((input || '').toLowerCase());
              }}
            >
              {jugadores.map(jugador => (
                <Select.Option key={jugador.jcl_id} value={jugador.jcl_id}>
                  {jugador.jcl_nombre} {jugador.jcl_apellido}
                </Select.Option>
              ))}
            </Select>
          </div>

          {estadoCuenta && jugadorSeleccionadoObj && (
            <>
              <Divider style={{ borderColor: '#434343', margin: '16px 0' }} />
              
              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ color: '#ffffff', display: 'block', marginBottom: 12 }}>
                  Estado de cuenta - {jugadorSeleccionadoObj.jcl_nombre} {jugadorSeleccionadoObj.jcl_apellido}
                </Text>
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text style={{ color: '#b0b0b0' }}>Total deuda:</Text>
                    <Text strong style={{ color: '#ff4d4f' }}>
                      ${parseFloat(estadoCuenta.total_deuda || 0).toFixed(2)}
                    </Text>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text style={{ color: '#b0b0b0' }}>Total pagos:</Text>
                    <Text strong style={{ color: '#52c41a' }}>
                      ${parseFloat(estadoCuenta.total_pagos || 0).toFixed(2)}
                    </Text>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, borderTop: '1px solid #434343' }}>
                    <Text strong style={{ color: '#ffffff', fontSize: '16px' }}>Saldo actual:</Text>
                    <Text strong style={{ 
                      color: estadoCuenta.saldo_actual > 0 ? '#ff4d4f' : '#52c41a',
                      fontSize: '16px'
                    }}>
                      ${parseFloat(estadoCuenta.saldo_actual || 0).toFixed(2)}
                    </Text>
                  </div>
                </Space>
              </div>

              <Divider style={{ borderColor: '#434343', margin: '16px 0' }} />

              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ color: '#ffffff', display: 'block', marginBottom: 12 }}>
                  Movimientos
                </Text>
                <Table
                  columns={columnsMovimientos}
                  dataSource={movimientosParaTabla}
                  loading={loading}
                  rowKey="ccm_id"
                  pagination={{ pageSize: 10, size: 'small' }}
                  size="small"
                  style={{ backgroundColor: '#1a1a1a' }}
                />
              </div>

              {hasPermission('cuenta_corriente', 'crear') && (
                <>
                  <Divider style={{ borderColor: '#434343', margin: '16px 0' }} />

                  <div>
                    <Text strong style={{ color: '#ffffff', display: 'block', marginBottom: 12 }}>
                      Registrar pago
                    </Text>
                    <Form form={form} layout="vertical">
                      <Form.Item 
                        label="Monto" 
                        required
                        validateStatus={montoPago > parseFloat(estadoCuenta.saldo_actual || 0) ? 'error' : ''}
                        help={montoPago > parseFloat(estadoCuenta.saldo_actual || 0) ? `El monto no puede ser mayor al saldo actual ($${parseFloat(estadoCuenta.saldo_actual || 0).toFixed(2)})` : ''}
                      >
                        <InputNumber
                          style={{ width: '100%' }}
                          placeholder={`Máximo: $${parseFloat(estadoCuenta.saldo_actual || 0).toFixed(2)}`}
                          value={montoPago}
                          onChange={(value) => {
                            const numValue = value || 0;
                            const saldoMax = parseFloat(estadoCuenta.saldo_actual || 0);
                            // Limitar automáticamente al saldo máximo
                            if (numValue > saldoMax) {
                              setMontoPago(saldoMax);
                              message.warning(`El monto se ha ajustado al saldo máximo: $${saldoMax.toFixed(2)}`);
                            } else {
                              setMontoPago(numValue);
                            }
                          }}
                          min={0}
                          max={parseFloat(estadoCuenta.saldo_actual || 0)}
                          precision={2}
                          prefix="$"
                        />
                      </Form.Item>
                      <Form.Item label="Forma de pago" required>
                        <Select
                          placeholder="Seleccione forma de pago"
                          value={formaPagoSeleccionada}
                          onChange={(value) => setFormaPagoSeleccionada(value)}
                          style={{ width: '100%' }}
                        >
                          {formasPago.map(fp => (
                            <Select.Option key={fp.fop_id} value={fp.fop_id}>
                              {fp.fop_nombre}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                      <Button
                        type="primary"
                        icon={<CheckOutlined />}
                        onClick={handleRegistrarPago}
                        loading={loadingPago}
                        block
                        disabled={!montoPago || montoPago <= 0 || montoPago > parseFloat(estadoCuenta.saldo_actual || 0) || !formaPagoSeleccionada}
                      >
                        Registrar Pago
                      </Button>
                    </Form>
                  </div>
                </>
              )}
            </>
          )}

          {!estadoCuenta && jugadorSeleccionado && (
            <div style={{ textAlign: 'center', padding: '20px 0', color: '#b0b0b0' }}>
              <ReloadOutlined spin style={{ fontSize: 24, marginBottom: 8 }} />
              <div>Cargando estado de cuenta...</div>
            </div>
          )}

          {!jugadorSeleccionado && (
            <div style={{ textAlign: 'center', padding: '20px 0', color: '#b0b0b0' }}>
              Seleccione un jugador para ver su estado de cuenta
            </div>
          )}
        </Card>

        {/* Tabla de jugadores con saldo deudor */}
        <Card
          style={{
            backgroundColor: '#2d2d2d',
            border: '1px solid #434343',
            borderRadius: 8,
          }}
          headStyle={{
            backgroundColor: '#2d2d2d',
            borderBottom: '1px solid #434343',
            color: '#ffffff',
          }}
          bodyStyle={{
            backgroundColor: '#2d2d2d',
            color: '#e0e0e0',
          }}
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: '#ffffff' }}>Jugadores con Saldo Deudor</span>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadJugadoresConSaldo}
                loading={loadingJugadoresSaldo}
                size="small"
              >
                Actualizar
              </Button>
            </div>
          }
        >
          <Table
            columns={columnsJugadoresSaldo}
            dataSource={jugadoresConSaldo}
            loading={loadingJugadoresSaldo}
            rowKey="jcl_id"
            pagination={{ pageSize: 10, size: 'small' }}
            size="small"
            style={{ backgroundColor: '#1a1a1a' }}
            locale={{
              emptyText: 'No hay jugadores con saldo deudor'
            }}
          />
        </Card>

        {/* Modal detalle movimiento (ticket/venta o pago) */}
        <Modal
          title="Detalle del movimiento"
          open={modalDetalleVisible}
          onCancel={() => { setModalDetalleVisible(false); setDetalleMovimiento(null); }}
          footer={null}
          width={isMobile ? '95%' : 600}
        >
          {loadingDetalle && (
            <div style={{ textAlign: 'center', padding: 24 }}><ReloadOutlined spin /> Cargando...</div>
          )}
          {!loadingDetalle && detalleMovimiento?.tipo === 'pago_manual' && (
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Fecha">{dayjs(detalleMovimiento.movimiento.ccm_fecha).format('DD/MM/YYYY HH:mm')}</Descriptions.Item>
              <Descriptions.Item label="Tipo"><Tag color="green">PAGO</Tag></Descriptions.Item>
              <Descriptions.Item label="Monto">${Number(detalleMovimiento.movimiento.ccm_monto).toFixed(2)}</Descriptions.Item>
              <Descriptions.Item label="Observaciones">{detalleMovimiento.movimiento.ccm_observaciones || '-'}</Descriptions.Item>
            </Descriptions>
          )}
          {!loadingDetalle && detalleMovimiento?.tipo === 'venta' && detalleMovimiento.ventas?.length > 0 && (
            <div>
              {detalleMovimiento.ventas.map((venta) => (
                <div key={venta.ven_id} style={{ marginBottom: 24 }}>
                  <Text strong style={{ color: '#ffffff', display: 'block', marginBottom: 8 }}>Venta #{venta.ven_id}</Text>
                  <Descriptions column={1} bordered size="small">
                    <Descriptions.Item label="Fecha">{venta.ven_fecha_creacion ? dayjs(venta.ven_fecha_creacion).format('DD/MM/YYYY HH:mm') : dayjs(venta.ven_fecha).format('DD/MM/YYYY')}</Descriptions.Item>
                    <Descriptions.Item label="Método de pago"><Tag>{venta.ven_metodo_pago || '-'}</Tag></Descriptions.Item>
                    <Descriptions.Item label="Total">${parseFloat(venta.ven_total || 0).toFixed(2)}</Descriptions.Item>
                    <Descriptions.Item label="Destino">
                      {(venta.ven_tipo_destino || 'mostrador') === 'cancha' && <Tag color="blue">Cancha: {venta.cancha_nombre || '-'}</Tag>}
                      {(venta.ven_tipo_destino || 'mostrador') === 'mesa' && <Tag color="purple">Mesa: {venta.mesa_nombre || '-'}</Tag>}
                      {(venta.ven_tipo_destino || 'mostrador') === 'mostrador' && <Tag>Mostrador</Tag>}
                    </Descriptions.Item>
                  </Descriptions>
                  {(venta.detalles && venta.detalles.length > 0) && (
                    <>
                      <Divider style={{ margin: '12px 0' }} />
                      <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>Artículos</Text>
                      <Table
                        dataSource={venta.detalles}
                        rowKey="ved_id"
                        pagination={false}
                        size="small"
                        columns={[
                          { title: 'Artículo', dataIndex: 'articulo_nombre', key: 'articulo_nombre' },
                          { title: 'Cant.', dataIndex: 'ved_cantidad', key: 'ved_cantidad', width: 70 },
                          { title: 'P. unit.', dataIndex: 'ved_precio_unitario', key: 'ved_precio_unitario', render: (p) => `$${parseFloat(p).toFixed(2)}`, width: 90 },
                          { title: 'Subtotal', dataIndex: 'ved_subtotal', key: 'ved_subtotal', render: (s) => `$${parseFloat(s).toFixed(2)}`, width: 90 },
                        ]}
                      />
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
          {!loadingDetalle && detalleMovimiento?.tipo === 'otro' && (
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Fecha">{dayjs(detalleMovimiento.movimiento.ccm_fecha).format('DD/MM/YYYY HH:mm')}</Descriptions.Item>
              <Descriptions.Item label="Tipo"><Tag color={detalleMovimiento.movimiento.ccm_tipo_movimiento === 'DEUDA' ? 'red' : 'green'}>{detalleMovimiento.movimiento.ccm_tipo_movimiento}</Tag></Descriptions.Item>
              <Descriptions.Item label="Monto">${Number(detalleMovimiento.movimiento.ccm_monto).toFixed(2)}</Descriptions.Item>
              <Descriptions.Item label="Observaciones">{detalleMovimiento.movimiento.ccm_observaciones || '-'}</Descriptions.Item>
            </Descriptions>
          )}
        </Modal>
      </div>
    </MainLayout>
  );
};

export default CuentaCorriente;
